// Offers — §9's third section, and the v2 home of offer administration.
//
// PRESERVES, from the v1 `/settings` page:
//   Business Units   GET/POST /api/business-units
//   Networks         GET/POST /api/networks
//
// WHAT THIS FILE BECAME (docs/v2/pages/SETTINGS_OFFERS.md, Phase A)
// =================================================================
// It used to be a 25-row read-only preview with a link out to the legacy
// `/offers` page, and a comment saying "Offer records are listed with a link
// out rather than an editor, because the editor is /offers and it still works."
// That was the right call for the night it was written and is the wrong one
// now: SETTINGS_OFFERS.md §2 sets the acceptance bar as **this page must do
// everything the legacy page does, so the legacy page can be switched off**,
// and a link out is the one thing that cannot satisfy it.
//
// So the Offers block below is the replacement: search, vertical filter, show
// archived, export, create, edit, assign, archive, restore, and the incomplete
// flag. The legacy `/offers` link is gone.
//
// THE `/campaigns/register` LINK IS GONE — the ruling above was overturned
// ========================================================================
// It used to read: "`/campaigns/register` is campaign registration's ONLY path
// in v2 and no replacement exists. Removing it would delete a capability in the
// name of tidying a link." That was correct while the capability existed.
// CAMPAIGNS-REMOVAL-EXECUTE (2026-09-02) removed campaign registration itself,
// so the premise dissolved and the link went with it. The overturn was
// deliberate — see docs/audit/TOMBSTONE_CAMPAIGNS.md. There are now no legacy
// links left on this surface.
//
// PHASE B IS NOT BUILT HERE
// =========================
// §10a splits this page. Multiple assigned media buyers, payout history and
// permanent delete are Phase B — each changes what an offer MEANS to the rest
// of Cipher, and each has an unresolved question in front of it. Nothing below
// anticipates them: assignment is single and Founder-only exactly as the
// endpoint is today, payout is a plain edit, and there is no delete control at
// all. A disabled button promising a Phase B action would be a worse lie than
// its absence.
//
// WHY BUSINESS UNITS AND NETWORKS ARE HERE — a placement call, stated not hidden
// ==============================================================================
// Both are settings on the v1 page, so both must be reachable in the new
// Settings; that is the function-preservation test and it is not optional. But
// §9 does not name either one, and §9.1 says plainly: "no Businesses/Workspaces
// concept".
//
// Read together with §3.11 those are consistent, and the resolution is that
// §9.1 forbids a *Businesses/Workspaces product concept* — the multi-tenant
// admin, the workspace switcher, the invite-and-role console — not the two
// small lists of operating reference data that already exist. §3.11 makes the
// same distinction for the same objects: `/select-bu` is REMOVE **as a
// surface**, while `BUContext` stays because other components consume it. The
// data lives; the product concept does not.
//
// Given they must live somewhere, they belong with Offers: business units and
// networks are the arm and the traffic source that every offer and campaign
// hangs off, and an operator registering a campaign is the operator who needs
// them. The alternative homes are Connections (wrong for business units, which
// are not an external system) and System Configuration (where two lists an
// operator uses weekly would be buried under governance).
//
// THE WORKSTATION IS STILL WHERE OFFERS ARE WORKED ON
// ===================================================
// §3.10 sends only the CRUD half of `/offers` here, and that is still true.
// Creating and editing an offer's core record is administration. Analysing it,
// buying traffic for it and building creative for it are the Workstation's, and
// none of that is reproduced below.
import React from "react";

import { ENDPOINTS, get, offersExportUrl, patch, post, query } from "../api";
import { isFounder } from "../sections";
import { Block, Gate, Table, Value } from "../ui";
import useResource from "../useResource";

const emptyList = (d) => !(Array.isArray(d) ? d : d?.offers || d?.campaigns || []).length;
const asList = (d) => (Array.isArray(d) ? d : d?.offers || d?.campaigns || []);

const TRACKING_PLATFORMS = [
  ["CAKE", "Cake"],
  ["EVERFLOW", "Everflow"],
];

const VERTICAL_CLASSES = ["LEADGEN", "ECOM"];

const CHANNELS = ["META", "TIKTOK", "GOOGLE", "NATIVE", "EMAIL", "SEARCH"];

const PAYOUT_TYPES = ["CPA", "CPS", "CPL", "RevShare"];

/**
 * The statuses `OFFER_STATUSES` allows, as the edit form offers them.
 *
 * Deliberately the same list the server validates against (`core.py`), not a
 * subset chosen for tidiness — an operator who cannot set a status here would
 * go back to the legacy page for it, which is the thing this page exists to
 * stop.
 */
const STATUSES = [
  "pending",
  "discovered",
  "assigned",
  "activated",
  "in_creative",
  "in_market",
  "paused",
  "closed",
];

// ── the incomplete flag (§7.1) ──────────────────────────────────────────────
//
// Computed HERE, on data `GET /offers` already returns, rather than asked of a
// second backend flag. Every input is on the `Offer` model and on the wire:
// `name`, `retail_price`, `cogs_estimate`, `product_url`, `source_urls`, and
// now `tracking_platform`.
//
// §7.1 asks for two meanings under ONE flag, not two flags. Legacy `incomplete`
// meant "Cipher's scrape came back short" — the `_is_offer_complete` rule the
// backend applies to `?incomplete=`. The new meaning is "anything Cipher needs
// to operate on this offer is missing", and platform is the one that matters
// most, because Tracking & Setup cannot validate an offer whose platform is
// unknown. Both conditions are reported as reasons on the same marker.

/** The backend's own `_is_offer_complete`, restated on the client. */
function hasMinimumDetail(o) {
  const hasName = !!(o.name && o.name.trim().length > 2);
  const hasPrice = !!(o.retail_price || o.cogs_estimate);
  const hasUrl = !!(o.product_url || (o.source_urls && o.source_urls.length > 0));
  return hasName && (hasPrice || hasUrl);
}

/** What is missing, in words. Empty means complete. */
export function incompleteReasons(o) {
  const missing = [];
  if (!o.tracking_platform) missing.push("tracking platform");
  if (!hasMinimumDetail(o)) missing.push("product detail");
  return missing;
}

// ── small helpers ───────────────────────────────────────────────────────────

function shortDate(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toISOString().slice(0, 10);
}

function money(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? String(v) : n.toFixed(2);
}

/** Copy to clipboard where the browser allows it; never throw at the operator. */
async function copy(text) {
  try {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    /* falls through to false — the code is still selectable on screen */
  }
  return false;
}

function Field({ label, hint, children }) {
  return (
    <label className="set-field">
      <span className="set-field-label">
        {label}
        {hint ? <span className="dim"> {hint}</span> : null}
      </span>
      {children}
    </label>
  );
}

function Select({ value, onChange, options, placeholder, disabled }) {
  return (
    <select
      className="set-input"
      value={value === null || value === undefined ? "" : value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{placeholder || "—"}</option>
      {options.map((o) => {
        const [v, label] = Array.isArray(o) ? o : [o, o];
        return (
          <option key={v} value={v}>
            {label}
          </option>
        );
      })}
    </select>
  );
}

/** Multi-line input. The Add Offer form had none — every field was single-line,
 *  including "Rules of engagement", which is why nobody could write more than a
 *  sentence anywhere on this form. */
function Textarea({ value, onChange, placeholder, rows = 5, disabled }) {
  return (
    <textarea
      className="set-input set-textarea"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      disabled={disabled}
    />
  );
}

function Text({ value, onChange, placeholder, type, disabled }) {
  return (
    <input
      className="set-input"
      type={type || "text"}
      value={value === null || value === undefined ? "" : value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** A stated failure. Never a silent one — R5's rule applied to writes. */
function Problem({ children }) {
  return children ? (
    <div className="state set-state set-state-error" role="alert">
      {children}
    </div>
  ) : null;
}

// ── create ──────────────────────────────────────────────────────────────────

const BLANK_OFFER = {
  offer_name: "",
  offer_link: "",
  network: "",
  vertical: "",
  tracking_platform: "",
  sub_category: "",
  commission: "",
  run_times: "247",
  daily_cap: "",
  channel: "META",
  rules_of_engagement: "",
  offer_description: "",
  business_unit: "AFFILIATE",
};

/**
 * Add Offer.
 *
 * THE DUPLICATE-NAME WARNING IS A PRE-CHECK, NOT A REJECTED POST
 * ==============================================================
 * §3.3 says a duplicate name warns and requires confirmation. The obvious
 * implementation — POST, let the server answer 409, POST again on confirm — is
 * the one thing that must not be built here, because `POST /offers/manual`
 * queues four background tasks AFTER it responds: an outbound scrape, a Ghost
 * Recon session that inserts documents, a metered Claude call, and a Google
 * Drive folder creation with a Telegram notification. There is no idempotency
 * key and no server-side duplicate check, so a rejected-then-retried create
 * runs all four twice and spends real money doing it.
 *
 * So the check happens BEFORE anything is created: `GET /offers?search=<name>`,
 * warn, and then exactly one POST on confirm.
 *
 * The submit button disables the moment it is pressed for the same reason. The
 * legacy form does not, so a double-click there has this effect today.
 *
 * A PRE-CHECK THAT COULD NOT RUN IS SAID OUT LOUD
 * ===============================================
 * If the search request itself fails, the operator is told the check could not
 * run and asked to confirm anyway. Treating a failed check as "no duplicates"
 * would silently downgrade the warning to nothing on exactly the days the
 * backend is unwell.
 */
function AddOffer({ networks, onCreated }) {
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState(BLANK_OFFER);
  const [busy, setBusy] = React.useState(false);
  const [dupes, setDupes] = React.useState(null);
  const [checkFailed, setCheckFailed] = React.useState(false);
  const [problem, setProblem] = React.useState(null);
  const [created, setCreated] = React.useState(null);

  const set = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    // Any edit invalidates a warning that was raised about the old values.
    setDupes(null);
    setCheckFailed(false);
  };

  const ready =
    form.offer_name.trim() &&
    form.offer_link.trim() &&
    form.network.trim() &&
    form.vertical &&
    form.tracking_platform &&
    form.sub_category.trim() &&
    form.commission !== "";

  function reset() {
    setForm(BLANK_OFFER);
    setDupes(null);
    setCheckFailed(false);
    setProblem(null);
  }

  async function submit(confirmed) {
    if (busy || !ready) return;
    setBusy(true);
    setProblem(null);
    setCreated(null);
    try {
      const name = form.offer_name.trim();
      if (!confirmed) {
        const res = await get(ENDPOINTS.offers, { search: name, include_deleted: true });
        if (res.status !== "ok") {
          setCheckFailed(true);
          return;
        }
        const matches = asList(res.data).filter(
          (o) => (o.name || "").trim().toLowerCase() === name.toLowerCase()
        );
        if (matches.length) {
          setDupes(matches);
          return;
        }
      }

      const res = await post(ENDPOINTS.offerManual, {
        offer_name: name,
        offer_link: form.offer_link.trim(),
        network: form.network.trim(),
        vertical: form.vertical,
        tracking_platform: form.tracking_platform,
        sub_category: form.sub_category.trim(),
        commission: parseFloat(form.commission),
        run_times: form.run_times || "247",
        daily_cap: form.daily_cap === "" ? null : parseInt(form.daily_cap, 10),
        channel: form.channel,
        rules_of_engagement: form.rules_of_engagement.trim() || null,
        offer_description: form.offer_description.trim() || null,
        business_unit: form.business_unit,
      });
      if (res.status !== "ok") {
        setProblem(
          res.status === "denied"
            ? "The server refused this. Creating an offer is Founder or media buyer."
            : res.error || "The offer was not created."
        );
        return;
      }
      setCreated(res.data);
      reset();
      setOpen(false);
      onCreated();
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <>
        <button type="button" className="btn-ghost btn-sm" onClick={() => setOpen(true)}>
          Add offer
        </button>
        {created ? (
          <div className="state set-state" role="status">
            Created <strong>{created.name}</strong> as{" "}
            <span className="mono">{created.offer_code}</span>. The code is generated and
            permanent.
          </div>
        ) : null}
      </>
    );
  }

  return (
    <div className="set-form" role="group" aria-label="Add offer">
      <p className="dim set-form-note">
        The offer code is generated on save from the sub-category, and can never be
        changed afterwards. It is never typed.
      </p>

      <div className="set-form-grid">
        <Field label="Offer name">
          <Text value={form.offer_name} onChange={set("offer_name")} disabled={busy} />
        </Field>
        <Field label="Offer link">
          <Text
            value={form.offer_link}
            onChange={set("offer_link")}
            type="url"
            disabled={busy}
          />
        </Field>
        {/* Deliberately optional. A required field here becomes "n/a" within a
            week, which is worse than empty because it looks filled. */}
        <Field
          label="What is this offer?"
          hint="optional, but it is what Cipher's research and creative work from"
        >
          <Textarea
            value={form.offer_description}
            onChange={set("offer_description")}
            placeholder="In your own words — what is sold, who buys it, and what problem it solves. As much detail as you want to give."
            rows={5}
          />
        </Field>

        <Field label="Network">
          <>
            <input
              className="set-input"
              list="v2-offer-networks"
              value={form.network}
              disabled={busy}
              onChange={(e) => set("network")(e.target.value)}
            />
            <datalist id="v2-offer-networks">
              {networks.map((n) => (
                <option key={n.network_id || n.name} value={n.name} />
              ))}
            </datalist>
          </>
        </Field>
        <Field label="Vertical" hint="LEADGEN or ECOM">
          <Select
            value={form.vertical}
            onChange={set("vertical")}
            options={VERTICAL_CLASSES}
            placeholder="Select…"
            disabled={busy}
          />
        </Field>
        <Field label="Tracking platform" hint="required">
          <Select
            value={form.tracking_platform}
            onChange={set("tracking_platform")}
            options={TRACKING_PLATFORMS}
            placeholder="Select…"
            disabled={busy}
          />
        </Field>
        <Field label="Sub-category" hint="the offer code's prefix">
          <Text value={form.sub_category} onChange={set("sub_category")} disabled={busy} />
        </Field>
        <Field label="Commission">
          <Text
            value={form.commission}
            onChange={set("commission")}
            type="number"
            disabled={busy}
          />
        </Field>
        <Field label="Run times" hint="24/7 unless the network restricts hours">
          <Text value={form.run_times} onChange={set("run_times")} disabled={busy} />
        </Field>
        <Field label="Daily cap" hint="optional">
          <Text
            value={form.daily_cap}
            onChange={set("daily_cap")}
            type="number"
            disabled={busy}
          />
        </Field>
        <Field label="Channel">
          <Select
            value={form.channel}
            onChange={set("channel")}
            options={CHANNELS}
            placeholder="Select…"
            disabled={busy}
          />
        </Field>
        <Field label="Business unit">
          <Text
            value={form.business_unit}
            onChange={set("business_unit")}
            disabled={busy}
          />
        </Field>
        <Field label="Rules of engagement" hint="optional">
          <Text
            value={form.rules_of_engagement}
            onChange={set("rules_of_engagement")}
            disabled={busy}
          />
        </Field>
      </div>

      {dupes ? (
        <div className="state set-state set-warn" role="alert">
          <strong>An offer with this name already exists.</strong>{" "}
          <span className="dim">
            {dupes
              .map((o) => `${o.offer_code}${o.is_deleted ? " (archived)" : ""}`)
              .join(", ")}
            . The same offer legitimately runs on more than one network, and the offer
            code distinguishes them — this is a warning, not a refusal.
          </span>
        </div>
      ) : null}

      {checkFailed ? (
        <div className="state set-state set-warn" role="alert">
          <strong>The duplicate check could not run.</strong>{" "}
          <span className="dim">
            Nothing has been created. Creating now goes ahead without that check.
          </span>
        </div>
      ) : null}

      <Problem>{problem}</Problem>

      <div className="set-form-actions">
        <button
          type="button"
          className="btn-ghost btn-sm"
          disabled={busy}
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-ghost btn-sm set-btn"
          disabled={busy || !ready}
          onClick={() => submit(!!(dupes || checkFailed))}
        >
          {busy
            ? "Creating…"
            : dupes || checkFailed
            ? "Create anyway"
            : "Create offer"}
        </button>
      </div>
    </div>
  );
}

// ── edit ────────────────────────────────────────────────────────────────────

/**
 * The editable shape, and which keys may be cleared to empty.
 *
 * `PATCH /offers/{id}` now distinguishes an omitted key from an explicit null:
 * omitted leaves the field alone, null clears it. Five fields are refused a
 * null server-side because they are non-optional on the `Offer` model — name,
 * payout, payout_type, status, geo — so this form never sends one for them.
 * Emptying a required box simply leaves it unchanged rather than producing a
 * 400 the operator did not ask for.
 */
const EDITABLE = [
  ["name", false],
  ["source_url", true],
  ["product_url", true],
  ["network_id", true],
  ["vertical_class", true],
  ["tracking_platform", true],
  ["sub_category", true],
  ["vertical", true],
  ["payout", false],
  ["payout_type", false],
  ["status", false],
  ["channel", true],
  ["run_times", true],
  ["daily_cap", true],
  ["rules_of_engagement", true],
  ["notes", true],
];

const NUMERIC = new Set(["payout", "daily_cap"]);

function EditOffer({ offer, networks, verticals, onDone, onCancel }) {
  const [form, setForm] = React.useState(() => {
    const f = {};
    EDITABLE.forEach(([k]) => {
      f[k] = offer[k] === null || offer[k] === undefined ? "" : String(offer[k]);
    });
    return f;
  });
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState(null);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  /** Only what changed, with empty meaning "clear" where clearing is allowed. */
  function body() {
    const out = {};
    EDITABLE.forEach(([k, clearable]) => {
      const was = offer[k] === null || offer[k] === undefined ? "" : String(offer[k]);
      const now = form[k];
      if (now === was) return;
      if (now === "") {
        if (clearable) out[k] = null;
        return; // a required field left empty is left alone, not nulled
      }
      out[k] = NUMERIC.has(k) ? Number(now) : now;
    });
    return out;
  }

  async function save() {
    if (busy) return;
    const payload = body();
    if (!Object.keys(payload).length) {
      onCancel();
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      const res = await patch(`${ENDPOINTS.offers}/${offer.offer_id}`, payload);
      if (res.status !== "ok") {
        setProblem(
          res.status === "denied"
            ? "The server refused this. Editing an offer is Founder or media buyer."
            : res.error || "The edit was not saved."
        );
        return;
      }
      onDone();
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr className="set-editrow">
      <td colSpan={9}>
        <div className="set-form" role="group" aria-label={`Edit ${offer.name}`}>
          <p className="dim set-form-note">
            Offer code <span className="mono">{offer.offer_code}</span> — permanent, and
            not editable here or by request. Everything else can be changed; an optional
            field left empty is cleared.
          </p>
          <div className="set-form-grid">
            <Field label="Offer name">
              <Text value={form.name} onChange={set("name")} disabled={busy} />
            </Field>
            <Field label="Offer link">
              <Text value={form.source_url} onChange={set("source_url")} disabled={busy} />
            </Field>
            <Field label="Product URL">
              <Text
                value={form.product_url}
                onChange={set("product_url")}
                disabled={busy}
              />
            </Field>
            {/* Deliberately optional. A required field here becomes "n/a" within a
            week, which is worse than empty because it looks filled. */}
        <Field
          label="What is this offer?"
          hint="optional, but it is what Cipher's research and creative work from"
        >
          <Textarea
            value={form.offer_description}
            onChange={set("offer_description")}
            placeholder="In your own words — what is sold, who buys it, and what problem it solves. As much detail as you want to give."
            rows={5}
          />
        </Field>

        <Field label="Network">
              <Select
                value={form.network_id}
                onChange={set("network_id")}
                options={networks.map((n) => [n.network_id, n.name])}
                placeholder="—"
                disabled={busy}
              />
            </Field>
            <Field label="Vertical" hint="LEADGEN or ECOM">
              <Select
                value={form.vertical_class}
                onChange={set("vertical_class")}
                options={VERTICAL_CLASSES}
                placeholder="—"
                disabled={busy}
              />
            </Field>
            <Field label="Tracking platform">
              <Select
                value={form.tracking_platform}
                onChange={set("tracking_platform")}
                options={TRACKING_PLATFORMS}
                placeholder="—"
                disabled={busy}
              />
            </Field>
            <Field label="Sub-category">
              <Text
                value={form.sub_category}
                onChange={set("sub_category")}
                disabled={busy}
              />
            </Field>
            <Field label="Classification" hint="the vertical column">
              <>
                <input
                  className="set-input"
                  list="v2-offer-verticals"
                  value={form.vertical}
                  disabled={busy}
                  onChange={(e) => set("vertical")(e.target.value)}
                />
                <datalist id="v2-offer-verticals">
                  {verticals.map((v) => (
                    <option key={v} value={v} />
                  ))}
                </datalist>
              </>
            </Field>
            <Field label="Payout">
              <Text
                value={form.payout}
                onChange={set("payout")}
                type="number"
                disabled={busy}
              />
            </Field>
            <Field label="Payout type">
              <Select
                value={form.payout_type}
                onChange={set("payout_type")}
                options={PAYOUT_TYPES}
                placeholder="—"
                disabled={busy}
              />
            </Field>
            <Field label="Status">
              <Select
                value={form.status}
                onChange={set("status")}
                options={STATUSES}
                placeholder="—"
                disabled={busy}
              />
            </Field>
            <Field label="Channel">
              <Select
                value={form.channel}
                onChange={set("channel")}
                options={CHANNELS}
                placeholder="—"
                disabled={busy}
              />
            </Field>
            <Field label="Run times" hint="24/7 unless the network restricts hours">
              <Text value={form.run_times} onChange={set("run_times")} disabled={busy} />
            </Field>
            <Field label="Daily cap">
              <Text
                value={form.daily_cap}
                onChange={set("daily_cap")}
                type="number"
                disabled={busy}
              />
            </Field>
            <Field label="Rules of engagement">
              <Text
                value={form.rules_of_engagement}
                onChange={set("rules_of_engagement")}
                disabled={busy}
              />
            </Field>
            <Field label="Notes">
              <Text value={form.notes} onChange={set("notes")} disabled={busy} />
            </Field>
          </div>
          <Problem>{problem}</Problem>
          <div className="set-form-actions">
            <button
              type="button"
              className="btn-ghost btn-sm"
              disabled={busy}
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-ghost btn-sm set-btn"
              disabled={busy}
              onClick={save}
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
}

// ── assign ──────────────────────────────────────────────────────────────────

/**
 * Assign, Founder-only — matching the endpoint, not widening it.
 *
 * §9's correction is explicit: rev 1 would have let a media buyer reassign any
 * offer to anyone. `PATCH /offers/{id}/assign` is `require_founder` today and
 * stays so, and this control is only rendered for the Founder. A non-founder
 * sees the assigned buyer's NAME (resolved server-side) and no control.
 *
 * ONE buyer, not several: multiple assignment is Phase B, and it is blocked on
 * a ruling — the same endpoint also resets the offer's status to `assigned`,
 * which would drop a live offer out of the portfolio.
 */
function Assign({ offer, operators, onDone, onCancel }) {
  const [who, setWho] = React.useState(offer.operator_id || "");
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState(null);

  async function save() {
    if (busy || !who) return;
    setBusy(true);
    setProblem(null);
    try {
      // `operator_id` is a QUERY parameter on this endpoint, not a body field.
      const res = await patch(
        `${ENDPOINTS.offers}/${offer.offer_id}/assign${query({ operator_id: who })}`,
        {}
      );
      if (res.status !== "ok") {
        setProblem(
          res.status === "denied"
            ? "The server refused this. Assignment is Founder-only."
            : res.error || "The assignment was not saved."
        );
        return;
      }
      onDone();
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr className="set-editrow">
      <td colSpan={9}>
        <div className="set-form" role="group" aria-label={`Assign ${offer.name}`}>
          <p className="dim set-form-note">
            Assigning also sets this offer's status to <span className="mono">assigned</span>.
            That is the endpoint's existing behaviour, stated rather than hidden.
          </p>
          <div className="set-form-grid">
            <Field label="Media buyer">
              <Select
                value={who}
                onChange={setWho}
                options={operators.map((o) => [
                  o.operator_id,
                  o.name || o.operator_code || o.operator_id,
                ])}
                placeholder="Select…"
                disabled={busy}
              />
            </Field>
          </div>
          <Problem>{problem}</Problem>
          <div className="set-form-actions">
            <button
              type="button"
              className="btn-ghost btn-sm"
              disabled={busy}
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-ghost btn-sm set-btn"
              disabled={busy || !who}
              onClick={save}
            >
              {busy ? "Assigning…" : "Assign"}
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
}

// ── pre-launch gate (#189) ──────────────────────────────────────────────────
//
// WHY THIS SURFACE
// ================
// The requirement is "ensure future ads use the correct naming convention and
// sub-ID structure BEFORE launch". A check is only a gate if it sits where the
// operator already is at that moment, and this row is it: the offer card is the
// one place in Cipher that hands over the campaign-name stem an operator pastes
// into Meta Ads Manager. The copy button in `OfferRow` below is the last thing
// Cipher does before Ads Manager opens. Putting the check anywhere else — or
// behind an endpoint with no caller, which is what happened to
// `POST /validate-name` (#176) — is the failure this exists to correct.
//
// TWO HALVES, TWO VERDICTS
// ========================
// A conforming name with no sub ids still cannot be joined by id, and a perfect
// template under a name the ingestion gate rejects still loses every row. So the
// two results are rendered separately and the operator is told which failed.
//
// THREE STATES THAT LOOK DIFFERENT
// ================================
// CONFORMING / FAILING / UNCHECKED get three different treatments — mark, word
// and colour. "Could not check" must never read as "fine"; that ambiguity is
// how an orphaned validator looked healthy for months.
//
// ADVISORY. There is no Save, no Apply, and no way to stop anything. The button
// says "Check" and that is all it does.

// Exported for the same reason `incompleteReasons` is: the requirement that the
// three states never render identically is decided by these three maps, so a
// test can assert it here rather than by screenshotting a browser.
export const STATE_LABEL = {
  CONFORMING: "conforming",
  FAILING: "does not conform",
  UNCHECKED: "not checked",
};

export const STATE_MARK = { CONFORMING: "✓", FAILING: "✕", UNCHECKED: "—" };

export const STATE_CLASS = {
  CONFORMING: "set-gate-pass",
  FAILING: "set-gate-fail",
  UNCHECKED: "set-gate-unchecked",
};

/** A copyable correct string. The whole point: an error that carries its fix. */
function CopyLine({ label, value, note }) {
  const [done, setDone] = React.useState(false);
  return (
    <div className="set-gate-copy">
      <div className="set-gate-copy-label">{label}</div>
      <div className="set-gate-copy-row">
        <code className="set-gate-copy-value">{value}</code>
        <button
          type="button"
          className="btn-ghost btn-sm set-copy"
          onClick={async () => setDone(await copy(value))}
          aria-label={`Copy ${label}`}
        >
          {done ? "copied" : "copy"}
        </button>
      </div>
      {note ? <div className="dim set-gate-copy-note">{note}</div> : null}
    </div>
  );
}

/** One half's verdict. Never collapses UNCHECKED into either of the others. */
function GateHalf({ title, result }) {
  if (!result) return null;
  const state = STATE_CLASS[result.state] ? result.state : "UNCHECKED";
  return (
    <div
      className={`set-gate-half ${STATE_CLASS[state]}`}
      data-testid={`gate-${result.half}`}
      data-state={state}
    >
      <div className="set-gate-head">
        <span className="set-gate-mark" aria-hidden="true">
          {STATE_MARK[state]}
        </span>
        <span className="set-gate-title">{title}</span>
        <span className="set-gate-state">{STATE_LABEL[state]}</span>
      </div>
      <p className="set-gate-reason">{result.reason}</p>
      {(result.problems || []).length ? (
        <ul className="set-gate-problems">
          {result.problems.map((p, i) => (
            <li key={i}>
              <span className="set-gate-problem">
                {p.slot ? <span className="mono">{p.slot}</span> : null}
                {p.slot ? " — " : null}
                {p.problem}
              </span>{" "}
              <span className="set-gate-fix">{p.fix}</span>
              {p.breaks_primary_join ? (
                <span className="set-flag"> breaks the id join</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {result.suggested_name ? (
        <CopyLine
          label="Use this instead"
          value={result.suggested_name}
          note={result.suggested_name_note}
        />
      ) : null}
      {result.accepted_forms && state === "FAILING" ? (
        <pre className="set-gate-forms">{result.accepted_forms.join("\n")}</pre>
      ) : null}
      {result.correct_template && state !== "CONFORMING" ? (
        <CopyLine
          label="Paste this into Meta"
          value={result.correct_template.url_parameters}
          note={result.where}
        />
      ) : null}
    </div>
  );
}

function PreLaunchCheck({ offer, onCancel }) {
  const [name, setName] = React.useState(offer.campaign_name_stem || "");
  const [params, setParams] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState(null);
  const [result, setResult] = React.useState(null);

  async function check() {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      const res = await post(ENDPOINTS.prelaunchCheck, {
        campaign_name: name,
        url_parameters: params,
      });
      if (res.status !== "ok") {
        // A failed request is NOT a failed check, and it is certainly not a
        // pass. Any previous result is cleared so nothing stale can be read as
        // current, and the message says plainly that nothing was checked.
        setResult(null);
        setProblem(
          res.status === "denied"
            ? "The server refused this check. Nothing was checked."
            : res.status === "disabled"
            ? "This check is not available on the server, so nothing was checked."
            : `${res.error || "The check did not run."} Nothing was checked.`
        );
        return;
      }
      setResult(res.data);
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr className="set-editrow">
      <td colSpan={9}>
        <div
          className="set-form"
          role="group"
          aria-label={`Pre-launch check for ${offer.name}`}
        >
          <p className="dim set-form-note">
            Check the campaign name and the Meta{" "}
            <span className="mono">URL parameters</span> field{" "}
            <strong>before</strong> you create the campaign. Advisory only: Cipher has
            no write path into Meta or RedTrack, so this changes nothing and blocks
            nothing. It also fixes nothing already running — campaigns already live
            stay as they are, and sub ids can never be filled in after the click.
          </p>
          <div className="set-form-grid">
            <Field label="Campaign name" hint="as you will type it in Meta">
              <Text
                value={name}
                onChange={setName}
                placeholder="Nick-OFFERCODE-CBO"
                disabled={busy}
              />
            </Field>
            <Field
              label="URL parameters"
              hint="Meta: Ad set / Ad → Tracking → URL parameters"
            >
              <Textarea
                value={params}
                onChange={setParams}
                rows={3}
                placeholder={"sub1={{ad.id}}&sub2={{adset.id}}&…"}
                disabled={busy}
              />
            </Field>
          </div>
          <Problem>{problem}</Problem>
          <div className="set-form-actions">
            <button
              type="button"
              className="btn-ghost btn-sm"
              disabled={busy}
              onClick={onCancel}
            >
              Close
            </button>
            <button
              type="button"
              className="btn-ghost btn-sm set-btn"
              disabled={busy}
              onClick={check}
            >
              {busy ? "Checking…" : "Check"}
            </button>
          </div>

          {result ? (
            <div className="set-gate" data-testid={`prelaunch-result-${offer.offer_id}`}>
              <div
                className={`set-gate-summary ${STATE_CLASS[result.overall] || ""}`}
                data-state={result.overall}
                role="status"
              >
                {result.summary}
              </div>
              <GateHalf title="Naming convention" result={result.naming} />
              <GateHalf title="Sub-ID structure" result={result.sub_ids} />
              <p className="dim set-gate-note">{result.existing_campaigns_note}</p>
            </div>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

// ── the list ────────────────────────────────────────────────────────────────

function OfferRow({
  offer,
  founder,
  onEdit,
  onAssign,
  onArchive,
  onRestore,
  onPreLaunch,
  busyId,
}) {
  const [copied, setCopied] = React.useState(false);
  const [stemCopied, setStemCopied] = React.useState(false);
  const missing = incompleteReasons(offer);
  const busy = busyId === offer.offer_id;

  return (
    <tr className={offer.is_deleted ? "set-row-archived" : undefined}>
      <td>
        <div>{offer.name || "—"}</div>
        {missing.length ? (
          <span className="set-flag" title={`Missing: ${missing.join(", ")}`}>
            incomplete — no {missing.join(", no ")}
          </span>
        ) : null}
        {offer.is_deleted ? <span className="set-flag dim">archived</span> : null}
      </td>
      <td className="mono">
        {offer.offer_code || "—"}
        {offer.offer_code ? (
          <button
            type="button"
            className="btn-ghost btn-sm set-copy"
            onClick={async () => setCopied(await copy(offer.offer_code))}
            aria-label={`Copy offer code ${offer.offer_code}`}
          >
            {copied ? "copied" : "copy"}
          </button>
        ) : null}
        {/* The campaign-name stem an operator pastes when launching this offer.
            Server-derived from the offer CODE (`campaign_name_stem`, built by
            routers/campaigns.offer_card_stem) so this card and the creative
            briefs cannot disagree, and so it arrives with the offer rather than
            costing a second request.

            What follows the stem is the BUDGET TYPE (CBO/ABO) and any tracking
            text — not the operator's initials. `is_valid_cipher_campaign_name`
            rejects initials appended at the end; they belong between the owner
            and the code (`Nick-SH-GRANT001`). Meta/RedTrack sync silently drops
            names it rejects, so the hint says which. */}
        <div className="set-stem" data-testid={`campaign-stem-${offer.offer_id}`}>
          {offer.campaign_name_stem ? (
            <>
              <span className="dim">campaign</span>{" "}
              <span>{offer.campaign_name_stem}</span>
              <button
                type="button"
                className="btn-ghost btn-sm set-copy"
                onClick={async () => setStemCopied(await copy(offer.campaign_name_stem))}
                aria-label={`Copy campaign name stem ${offer.campaign_name_stem}`}
                title="Paste this, then add the budget type (CBO/ABO) and any tracking text. Initials go before the code: Nick-SH-CODE"
              >
                {stemCopied ? "copied" : "copy"}
              </button>
            </>
          ) : null}
          {/* The pre-launch gate (#189), beside the copy button on purpose:
              this is the last moment Cipher has before the operator is in Meta
              Ads Manager finishing the name and filling in the URL parameters
              field. A check that is not here is a check nobody runs.

              Shown even when there is no stem — an offer with no code is
              exactly the one most likely to be launched under a name the
              ingestion gate drops. */}
          <button
            type="button"
            className="btn-ghost btn-sm set-copy"
            onClick={onPreLaunch}
            aria-label={`Pre-launch check for ${offer.name || offer.offer_code || offer.offer_id}`}
            title="Check the campaign name and the Meta URL parameters before you launch. Advisory — it blocks nothing."
          >
            pre-launch check
          </button>
        </div>
      </td>
      <td className="dim">{offer.vertical || "—"}</td>
      <td className="mono">{offer.status || "—"}</td>
      <td className="dim">{offer.assigned_operator_name || "—"}</td>
      <td>
        <Value>{money(offer.payout)}</Value>
      </td>
      <td className="dim">{shortDate(offer.created_at) || "—"}</td>
      <td className="dim">{shortDate(offer.updated_at) || "—"}</td>
      <td className="set-cell-action">
        <button type="button" className="btn-ghost btn-sm" disabled={busy} onClick={onEdit}>
          Edit
        </button>
        {founder ? (
          <button
            type="button"
            className="btn-ghost btn-sm"
            disabled={busy}
            onClick={onAssign}
          >
            Assign
          </button>
        ) : null}
        {offer.is_deleted ? (
          founder ? (
            <button
              type="button"
              className="btn-ghost btn-sm"
              disabled={busy}
              onClick={onRestore}
            >
              Restore
            </button>
          ) : null
        ) : (
          <button
            type="button"
            className="btn-ghost btn-sm"
            disabled={busy}
            onClick={onArchive}
          >
            Archive
          </button>
        )}
      </td>
    </tr>
  );
}

function Offers({ user }) {
  // `user` is the prop the dispatcher hands every section (`routes/settings.jsx`),
  // not a second read of the shell context. Founder gating decides what is
  // SHOWN; every endpoint below is gated on the server independently (R5).
  const founder = isFounder(user);

  const [searchBox, setSearchBox] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [vertical, setVertical] = React.useState("");
  const [showArchived, setShowArchived] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [assigning, setAssigning] = React.useState(null);
  // The pre-launch gate (#189). One row open at a time, like Edit and Assign.
  const [checking, setChecking] = React.useState(null);
  const [busyId, setBusyId] = React.useState(null);
  const [problem, setProblem] = React.useState(null);

  // The search box is debounced rather than fired per keystroke: `useResource`
  // re-reads whenever `params` changes by value, and an un-debounced box would
  // put one request on the wire per character typed.
  React.useEffect(() => {
    const t = setTimeout(() => setSearch(searchBox.trim()), 250);
    return () => clearTimeout(t);
  }, [searchBox]);

  const params = React.useMemo(() => {
    const p = {};
    if (search) p.search = search;
    if (vertical) p.vertical = vertical;
    if (showArchived) p.include_deleted = true;
    return p;
  }, [search, vertical, showArchived]);

  const offers = useResource(ENDPOINTS.offers, { params, isEmpty: emptyList });
  const verticals = useResource(ENDPOINTS.offerVerticals);
  const networks = useResource(ENDPOINTS.networks);
  // Founder-only on the server; skipped entirely for anybody else so a media
  // buyer never fires a request that is going to be refused.
  const operators = useResource(ENDPOINTS.operators, { skip: !founder });

  const networkList = asList(networks.data);
  const verticalList = (verticals.data && verticals.data.verticals) || [];
  const operatorList = (Array.isArray(operators.data) ? operators.data : []).filter(
    (o) => o.status !== "inactive"
  );

  async function act(offer, path, label) {
    setBusyId(offer.offer_id);
    setProblem(null);
    try {
      // An explicit empty body, matching what the legacy page sends. Neither
      // endpoint reads one, but axios omits the Content-Type header entirely
      // for `undefined`, and that is a difference worth not having.
      const res = await post(`${ENDPOINTS.offers}/${offer.offer_id}/${path}`, {});
      if (res.status !== "ok") {
        setProblem(
          res.status === "denied"
            ? `The server refused ${label}. ${
                path === "restore"
                  ? "Restore is Founder-only."
                  : "Archiving is Founder or media buyer."
              }`
            : res.error || `${label} did not complete.`
        );
        return;
      }
      offers.reload();
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Block
      title="Offers"
      note="The core record for each offer, and the whole of its administration. Working on an offer happens in the Workstation."
      actions={
        <>
          <a
            className="btn-ghost btn-sm"
            href={offersExportUrl(params)}
            data-testid="offers-export-csv"
          >
            Export CSV
          </a>
          <AddOffer networks={networkList} onCreated={offers.reload} />
        </>
      }
    >
      <div className="set-controls" role="group" aria-label="Filter offers">
        <label className="set-field set-field-inline">
          <span className="set-field-label">Search</span>
          <input
            className="set-input"
            type="search"
            value={searchBox}
            placeholder="name, code or id"
            onChange={(e) => setSearchBox(e.target.value)}
          />
        </label>
        <label className="set-field set-field-inline">
          <span className="set-field-label">Vertical</span>
          <Select
            value={vertical}
            onChange={setVertical}
            options={verticalList}
            placeholder="All"
          />
        </label>
        <label className="set-toggle">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          <span className="set-toggle-text">Show archived</span>
        </label>
      </div>

      <Problem>{problem}</Problem>

      <Gate resource={offers} empty="No offers match this view.">
        {(data) => (
          <Table
            head={[
              "Offer",
              "Code",
              "Vertical",
              "Status",
              "Media buyer",
              "Payout",
              "Created",
              "Last edited",
              "",
            ]}
          >
            {asList(data).map((o) => {
              const rows = [
                <OfferRow
                  key={o.offer_id}
                  offer={o}
                  founder={founder}
                  busyId={busyId}
                  onEdit={() => {
                    setAssigning(null);
                    setChecking(null);
                    setEditing(editing === o.offer_id ? null : o.offer_id);
                  }}
                  onAssign={() => {
                    setEditing(null);
                    setChecking(null);
                    setAssigning(assigning === o.offer_id ? null : o.offer_id);
                  }}
                  onPreLaunch={() => {
                    setEditing(null);
                    setAssigning(null);
                    setChecking(checking === o.offer_id ? null : o.offer_id);
                  }}
                  onArchive={() => act(o, "delete", "archiving")}
                  onRestore={() => act(o, "restore", "restoring")}
                />,
              ];
              if (editing === o.offer_id) {
                rows.push(
                  <EditOffer
                    key={`${o.offer_id}-edit`}
                    offer={o}
                    networks={networkList}
                    verticals={verticalList}
                    onCancel={() => setEditing(null)}
                    onDone={() => {
                      setEditing(null);
                      offers.reload();
                    }}
                  />
                );
              }
              if (assigning === o.offer_id) {
                rows.push(
                  <Assign
                    key={`${o.offer_id}-assign`}
                    offer={o}
                    operators={operatorList}
                    onCancel={() => setAssigning(null)}
                    onDone={() => {
                      setAssigning(null);
                      offers.reload();
                    }}
                  />
                );
              }
              if (checking === o.offer_id) {
                rows.push(
                  <PreLaunchCheck
                    key={`${o.offer_id}-prelaunch`}
                    offer={o}
                    onCancel={() => setChecking(null)}
                  />
                );
              }
              return rows;
            })}
          </Table>
        )}
      </Gate>
    </Block>
  );
}

function Campaigns() {
  const campaigns = useResource(ENDPOINTS.campaigns, { isEmpty: emptyList });

  return (
    <Block
      title="Campaigns"
      // The "Register a campaign" action was removed 2026-09-02
      // (CAMPAIGNS-REMOVAL-EXECUTE) along with registration itself. The comment
      // that kept it said registration "has no v2 replacement, and this is its
      // only path" — true while the capability existed; the capability is gone,
      // so the premise is gone with it. Deliberate, not an oversight.
      // See docs/audit/TOMBSTONE_CAMPAIGNS.md.
      note="Campaigns seen in spend data, including the ABH arm."
    >
      <Gate resource={campaigns} empty="No campaigns registered yet.">
        {(data) => (
          <Table head={["Campaign", "Business unit", "Status"]}>
            {asList(data)
              .slice(0, 25)
              .map((c) => (
                <tr key={c.campaign_id || c.id || c.name}>
                  <td>{c.name || c.campaign_name}</td>
                  <td className="dim">{c.business_unit || "—"}</td>
                  <td className="mono">{c.status || "—"}</td>
                </tr>
              ))}
          </Table>
        )}
      </Gate>
    </Block>
  );
}

function BusinessUnits() {
  const bus = useResource(ENDPOINTS.businessUnits, { isEmpty: emptyList });

  return (
    <Block
      title="Business units"
      note="The arms Cipher operates. Preserved from the v1 Settings page."
    >
      <Gate resource={bus} empty="No business units defined.">
        {(data) => (
          <Table head={["Name", "Description"]}>
            {asList(data).map((b) => (
              <tr key={b.bu_id || b.id || b.name}>
                <td>{b.name}</td>
                <td className="dim">{b.description || "—"}</td>
              </tr>
            ))}
          </Table>
        )}
      </Gate>
    </Block>
  );
}

function Networks() {
  const nets = useResource(ENDPOINTS.networks, { isEmpty: emptyList });

  return (
    <Block
      title="Networks"
      note="Affiliate networks offers run through. Preserved from the v1 Settings page."
    >
      <Gate resource={nets} empty="No networks defined.">
        {(data) => (
          <Table head={["Name", "Contact", "Notes"]}>
            {asList(data).map((n) => (
              <tr key={n.net_id || n.id || n.name}>
                <td>{n.name}</td>
                <td className="dim">{n.contact_name || n.contact || "—"}</td>
                <td className="dim">{n.notes || n.description || "—"}</td>
              </tr>
            ))}
          </Table>
        )}
      </Gate>
    </Block>
  );
}

export default function OffersSection({ user }) {
  return (
    <>
      <Offers user={user} />
      <Campaigns />
      <BusinessUnits />
      <Networks />
    </>
  );
}
